import notification from "antd/es/notification";
import { getLocation, push, RouterLocation } from "connected-react-router";
import endOfDay from "date-fns/endOfDay";
import { addDays, subWeeks } from "date-fns/esm";
import startOfDay from "date-fns/startOfDay";
import { all, call, delay, fork, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import ActivityStatusOption from "@mapmycustomers/shared/enum/activity/ActivityStatusOption";
import DealStageType from "@mapmycustomers/shared/enum/DealStageType";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import {
  Company,
  Deal,
  EntitiesSupportedByEmailFeature,
  EntityType,
  Person,
} from "@mapmycustomers/shared/types/entity";
import { OutOfCadenceEntity } from "@mapmycustomers/shared/types/entity/activities/OutOfCadenceEntity";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import Organization from "@mapmycustomers/shared/types/Organization";
import User from "@mapmycustomers/shared/types/User";
import FilterModel from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import ViewState from "@mapmycustomers/shared/types/viewModel/ViewState";
import { isApiError, isDefined } from "@mapmycustomers/shared/util/assert";
import { showModal } from "@mapmycustomers/ui";

import configService from "@app/config/ConfigService";
import i18nService from "@app/config/I18nService";
import localSettings from "@app/config/LocalSettings";
import IntervalUnit from "@app/enum/IntervalUnit";
import MmcNotificationAction from "@app/enum/MmcNotificationAction";
import MmcNotificationGroupType from "@app/enum/MmcNotificationGroupType";
import MmcNotificationRouteType from "@app/enum/MmcNotificationRouteType";
import Path from "@app/enum/Path";
import RecordPaneTab from "@app/enum/preview/RecordPaneTab";
import {
  applyListViewSettings as applyActivityListViewSettings,
  updateActivityView,
} from "@app/scene/activity/store/actions";
import ViewMode from "@app/scene/deal/enum/ViewMode";
import {
  applyListViewSettings as applyDealListViewSettings,
  updateDealView,
} from "@app/scene/deal/store/actions";
import OnboardingViewMode from "@app/scene/onboarding/enums/ViewMode";
import { applyTripListViewSettings } from "@app/scene/reports/store/actions";
import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { getCurrentUserId, getOrganizationId } from "@app/store/iam";
import { notifyAboutChanges } from "@app/store/uiSync/actions";
import MmcNotification from "@app/types/MmcNotification";
import NotificationListResponse from "@app/types/viewModel/NotificationListResponse";
import mapActivityStatusOptionToFilter from "@app/util/activity/mapActivityStatusOptionToFilter";
import { getLegacyAppUrl } from "@app/util/appUrl";
import { getWeekEnd, getWeekStart } from "@app/util/dates";
import activityFieldModel from "@app/util/fieldModel/ActivityFieldModel";
import dealFieldModel from "@app/util/fieldModel/DealFieldModel";
import tripFieldModel, { TripFieldName } from "@app/util/fieldModel/tripFieldModel";
import { parseApiDate } from "@app/util/parsers";

import authService from "../auth/AuthService";
import { getFunnelStages } from "../deal";
import { showEntityView } from "../entityView/actions";

import {
  clickOnNotification,
  fetchNotification,
  fetchNotifications,
  fetchNotificationsUnreadTotal,
  markNotificationsAsRead,
  showBouncedEmailModal,
  showCadenceModal,
  showNotifications,
  unsubscribeBouncedEmailEntity,
  updateBouncedEmailEntityEmail,
  updateNotificationReadStatus,
} from "./actions";
import { getBouncedEmailEntity, getNotifications, isOnlyMentions } from "./selectors";

const NOTIFICATION_PAGE_SIZE = 20;

const GROUP_URL_BY_TYPE: Record<MmcNotificationGroupType, EntityType> = {
  [MmcNotificationGroupType.COMPANY]: EntityType.COMPANY,
  [MmcNotificationGroupType.DEAL]: EntityType.DEAL,
  [MmcNotificationGroupType.PERSON]: EntityType.PERSON,
};

const ROUTE_URL_BY_TYPE: Record<MmcNotificationRouteType, string> = {
  [MmcNotificationRouteType.COMPANY]: "accounts",
  [MmcNotificationRouteType.PERSON]: "contacts",
};

export function* onFetchNotifications({
  payload: { updateTotalsOnly },
}: ReturnType<typeof fetchNotifications.request>) {
  try {
    const organizationId: Organization["id"] = yield select(getOrganizationId);
    const notifications: MmcNotification[] = yield select(getNotifications);
    const onlyMentions: MmcNotification[] = yield select(isOnlyMentions);
    const response: NotificationListResponse<MmcNotification> = yield callApi(
      "fetchNotifications",
      organizationId,
      {
        $filters: onlyMentions
          ? {
              $and: [{ action: { $in: [MmcNotificationAction.MENTION] } }],
            }
          : undefined,
        $limit: NOTIFICATION_PAGE_SIZE,
        $offset: notifications.length,
        $order: "-updatedAt",
      }
    );
    const availableActionTypes = new Set(Object.values(MmcNotificationAction));

    yield put(
      fetchNotifications.success({
        notifications: updateTotalsOnly
          ? []
          : // We need to provide only notification types which web supports.
            response.data.filter(({ action }) => availableActionTypes.has(action)),
        total: response.total,
        unreadTotal: response.unreadTotal,
      })
    );
  } catch (error) {
    yield put(fetchNotifications.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onGetNotification({
  payload: { id, callback },
}: ReturnType<typeof fetchNotification.request>) {
  try {
    const organizationId: Organization["id"] = yield select(getOrganizationId);
    const response: MmcNotification = yield callApi("getNotification", organizationId, id);
    callback?.(response);
    yield put(fetchNotification.success(response));
  } catch (error) {
    yield put(fetchNotification.failure(error));

    const hasNoAccess = isApiError(error) && error.status === 404;
    if (hasNoAccess) {
      const intl = i18nService.getIntl();
      if (intl) {
        notification.error({
          message: intl.formatMessage({
            id: "navbar.notifications.list.getNotification.noAccess",
            defaultMessage: "Cannot show notification as you are logged in to a different account.",
            description: "Navbar Notifications - list - cannot show notification error.",
          }),
        });
      }
    } else {
      yield put(handleError({ error }));
    }
  }
}

export function* onFetchNotificationsUnreadTotal() {
  try {
    const organizationId: Organization["id"] | undefined = yield select(getOrganizationId);
    if (organizationId) {
      const response: NotificationListResponse<MmcNotification> = yield callApi(
        "fetchNotifications",
        organizationId,
        {
          $limit: 0,
        }
      );
      yield put(fetchNotificationsUnreadTotal.success(response.unreadTotal));
    }
  } catch (error) {
    yield put(fetchNotificationsUnreadTotal.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onUpdateNotificationReadStatus({
  payload: { id, readStatus },
}: ReturnType<typeof updateNotificationReadStatus.request>) {
  try {
    const organizationId: Organization["id"] = yield select(getOrganizationId);
    yield callApi("updateNotifications", organizationId, [id], { readStatus });
    yield put(updateNotificationReadStatus.success());
    yield put(fetchNotificationsUnreadTotal.request());
    yield put(fetchNotifications.request({ updateTotalsOnly: true }));
  } catch (error) {
    yield put(updateNotificationReadStatus.failure(error));
    yield put(handleError({ error }));
  }
}
export function* onMarkNotificationsAsRead() {
  try {
    const onlyMentions: boolean = yield select(isOnlyMentions);
    const organizationId: Organization["id"] = yield select(getOrganizationId);
    yield callApi(
      "updateNotifications",
      organizationId,
      [],
      { readStatus: true },
      {
        $filters: onlyMentions ? { mention: true } : {},
      }
    );
    yield put(markNotificationsAsRead.success());
    yield put(fetchNotificationsUnreadTotal.request());
    yield put(fetchNotifications.request({ reload: true }));
  } catch (error) {
    yield put(markNotificationsAsRead.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onClickOnNotification({
  payload: { elementId, notification },
}: ReturnType<typeof clickOnNotification>) {
  const { action, count, entity, group, readStatus, route, updatedAt } = notification;
  const baseOldAppUrl = configService.getBaseOldAppUrl();
  const token = authService.getToken();
  const { pathname }: RouterLocation<any> = yield select(getLocation);
  const dealListPath = `${Path.DEAL}/${ViewMode.LIST}`;
  const activityListPath = `${Path.ACTIVITY}/${ViewMode.LIST}`;
  const mileageTrackingListPath = `${Path.REPORTS}/mileage-tracking`;
  const currentUserId: User["id"] = yield select(getCurrentUserId);

  if (!readStatus) {
    yield put(updateNotificationReadStatus.request({ id: notification.id, readStatus: true }));
  }

  if (action === MmcNotificationAction.UNCLASSIFIED_TRIPS) {
    const mileageTrackingViewState: ViewState = yield call(
      localSettings.getViewSettings,
      "mileageTracking/listView",
      tripFieldModel,
      tripFieldModel.getDefaultListViewState()
    );
    mileageTrackingViewState.filter = {
      [TripFieldName.TYPE]: { operator: FilterOperator.EMPTY, value: undefined },
      [TripFieldName.USER]: { operator: FilterOperator.CONTAINS, value: [currentUserId] },
    };
    mileageTrackingViewState.range = {
      endRow: mileageTrackingViewState.range.endRow - mileageTrackingViewState.range.startRow,
      startRow: 0,
    };
    yield call(localSettings.setViewSettings, mileageTrackingViewState, "mileageTracking/listView");
    if (pathname === mileageTrackingListPath) {
      yield put(applyTripListViewSettings(mileageTrackingViewState));
    } else {
      yield put(push(`${Path.REPORTS}/mileage-tracking`));
    }
    return;
  } else if (action === MmcNotificationAction.AUTO_TRACKED_TRIPS) {
    if (!count) {
      yield call(showModal, {
        centered: true,
        closable: true,
        content: i18nService.formatMessage(
          {
            id: "navbar.notifications.list.tripsNotification.nothingTracked",
            defaultMessage:
              "If you expected drives to be logged, double-check your device permissions. Go to Profile > Device Permissions in the mobile app to make sure everything’s enabled.",
            description: "Navbar Notifications - list - no trips tracked modal - content",
          },
          "If you expected drives to be logged, double-check your device permissions. Go to Profile > Device Permissions in the mobile app to make sure everything’s enabled."
        ),
        okButtonProps: {
          type: "primary",
        },
        okText: i18nService.formatMessage(
          {
            id: "navbar.notifications.list.tripsNotification.nothingTracked.ok",
            defaultMessage: "Okay",
            description: "Navbar Notifications - list - no trips tracked modal - ok button",
          },
          "Okay"
        ),
        title: i18nService.formatMessage(
          {
            id: "navbar.notifications.list.tripsNotification.nothingTracked.title",
            defaultMessage: "No drives tracked this week",
            description: "Navbar Notifications - list - no trips tracked modal - title",
          },
          "No drives tracked this week"
        ),
        type: "warning",
        width: "30rem",
      });
    } else {
      const weekStart = subWeeks(getWeekStart(parseApiDate(notification.updatedAt)), 1);
      const mileageTrackingViewState: ViewState = yield call(
        localSettings.getViewSettings,
        "mileageTracking/listView",
        tripFieldModel,
        tripFieldModel.getDefaultListViewState()
      );
      mileageTrackingViewState.filter = {
        [TripFieldName.START_TIME]: {
          operator: FilterOperator.IN_RANGE,
          value: [weekStart.toISOString(), getWeekEnd(weekStart).toISOString()],
        },
        [TripFieldName.TYPE]: { operator: FilterOperator.NOT_EMPTY, value: undefined },
        [TripFieldName.USER]: { operator: FilterOperator.CONTAINS, value: [currentUserId] },
      };
      mileageTrackingViewState.range = {
        endRow: mileageTrackingViewState.range.endRow - mileageTrackingViewState.range.startRow,
        startRow: 0,
      };
      yield call(
        localSettings.setViewSettings,
        mileageTrackingViewState,
        "mileageTracking/listView"
      );
      if (pathname === mileageTrackingListPath) {
        yield put(applyTripListViewSettings(mileageTrackingViewState));
      } else {
        yield put(push(`${Path.REPORTS}/mileage-tracking`));
      }
    }
    return;
  } else if (
    group &&
    [
      MmcNotificationAction.COMPANY_GROUP_SHARED,
      MmcNotificationAction.DEAL_GROUP_SHARED,
      MmcNotificationAction.PEOPLE_GROUP_SHARED,
    ].includes(action)
  ) {
    if (!readStatus) {
      yield put(updateNotificationReadStatus.request({ id: notification.id, readStatus: true }));
    }
    yield put(push(`${Path.MAP}/groups/${GROUP_URL_BY_TYPE[group.type]}/${group.id}`));
  } else if (
    token &&
    route &&
    [
      MmcNotificationAction.COMPANY_ROUTE_SHARED,
      MmcNotificationAction.PEOPLE_ROUTE_SHARED,
    ].includes(action)
  ) {
    if (!readStatus) {
      yield put(updateNotificationReadStatus.request({ id: notification.id, readStatus: true }));
    }
    window.location.href = getLegacyAppUrl(
      baseOldAppUrl,
      token,
      `${ROUTE_URL_BY_TYPE[route.type]}/routing/edit/${route.id}`
    );
  } else if (
    [MmcNotificationAction.GUIDE_ASSIGNED, MmcNotificationAction.GUIDE_REMINDER].includes(action)
  ) {
    yield put(push(`${Path.ONBOARDING}/${OnboardingViewMode.MY}`));
  }

  if (MmcNotificationAction.BOUNCED_EMAIL === action) {
    yield put(showBouncedEmailModal.request(notification));
  } else if (entity && entity.length === 1) {
    if (
      [
        MmcNotificationAction.ACTIVITY_ASSIGNED,
        MmcNotificationAction.ACTIVITY_LOGGED,
        MmcNotificationAction.ACTIVITY_NOTE_ADDED,
        MmcNotificationAction.ACTIVITY_NOTE_UPDATED,
        MmcNotificationAction.ACTIVITY_STARTING,
        MmcNotificationAction.ACTIVITY_TIME_CHANGED,
        MmcNotificationAction.DEALS_MARKED_LOST,
        MmcNotificationAction.DEALS_MARKED_WON,
        MmcNotificationAction.DEALS_ROTTING,
        MmcNotificationAction.DEALS_STAGE_CHANGED,
        MmcNotificationAction.INCOMPLETE_ACTIVITY,
        MmcNotificationAction.MARK_ACTIVITY_AS_DONE,
        MmcNotificationAction.MENTION,
        MmcNotificationAction.NOTE_ADDED,
        MmcNotificationAction.OUT_OF_CADENCE,
        MmcNotificationAction.OVERDUE_ACTIVITIES,
        MmcNotificationAction.OWNED_DEALS_CLOSING,
        MmcNotificationAction.OWNERSHIP_CHANGED,
        MmcNotificationAction.SHARED_DEALS_CLOSING,
      ].includes(action)
    ) {
      yield put(
        showEntityView({
          elementId,
          entityId: entity[0].id,
          entityType: entity[0].type,
          focusOnPin: true,
          tab: [MmcNotificationAction.MENTION, MmcNotificationAction.NOTE_ADDED].includes(action)
            ? RecordPaneTab.NOTES
            : undefined,
        })
      );
    } else if (MmcNotificationAction.RESOURCE_SHARED === action) {
      yield put(push(`${Path.RESOURCES}/files/${entity[0].id}`));
    } else if (MmcNotificationAction.FOLDER_SHARED === action) {
      yield put(push(`${Path.RESOURCES}/folders/${entity[0].id}`));
    }
  } else if (entity && entity.length > 1) {
    const funnelStages: Record<Funnel["id"], Stage[]> = yield select(getFunnelStages);
    if (MmcNotificationAction.DEALS_MARKED_LOST === action) {
      const closedLostStageIds = Object.values(funnelStages)
        .reduce<Stage[]>((result, stages) => [...result, ...stages], [])
        .filter(({ type }) => [DealStageType.LOST].includes(type))
        .map(({ id }) => id);
      const stateToUpdate = {
        filter: {
          stage: {
            operator: FilterOperator.IN_ANY,
            value: closedLostStageIds,
          },
        },
      };
      if (pathname === dealListPath) {
        yield put(
          updateDealView({
            viewState: stateToUpdate,
          })
        );
      } else {
        yield put(push(`${Path.DEAL}/${ViewMode.LIST}`));
        yield put(applyDealListViewSettings(stateToUpdate));
      }
    } else if (MmcNotificationAction.DEALS_MARKED_WON === action) {
      const closedWonStageIds = Object.values(funnelStages)
        .reduce<Stage[]>((result, stages) => [...result, ...stages], [])
        .filter(({ type }) => [DealStageType.WON].includes(type))
        .map(({ id }) => id);

      const stateToUpdate = {
        filter: {
          stage: {
            operator: FilterOperator.IN_ANY,
            value: closedWonStageIds,
          },
        },
      };
      if (pathname === dealListPath) {
        yield put(
          updateDealView({
            viewState: stateToUpdate,
          })
        );
      } else {
        yield put(push(`${Path.DEAL}/${ViewMode.LIST}`));
        yield put(applyDealListViewSettings(stateToUpdate));
      }
    } else if (MmcNotificationAction.DEALS_ROTTING === action) {
      const stateToUpdate = {
        filter: {
          rottingDaysOut: {
            operator: FilterOperator.IS_OVERDUE,
            value: undefined,
          },
        },
      };
      if (pathname === dealListPath) {
        yield put(
          updateDealView({
            viewState: stateToUpdate,
          })
        );
      } else {
        yield put(push(`${Path.DEAL}/${ViewMode.LIST}`));
        yield put(applyDealListViewSettings(stateToUpdate));
      }
    } else if (MmcNotificationAction.DEALS_STAGE_CHANGED === action) {
      const stateToUpdate = {
        filter: {},
        sort: [{ field: dealFieldModel.getByName("updatedAt")!, order: SortOrder.DESC }],
      };
      if (pathname === dealListPath) {
        yield put(updateDealView({ viewState: stateToUpdate }));
      } else {
        yield put(push(`${Path.DEAL}/${ViewMode.LIST}`));
        yield put(applyDealListViewSettings(stateToUpdate));
      }
    } else if (
      [
        MmcNotificationAction.OWNED_DEALS_CLOSING,
        MmcNotificationAction.SHARED_DEALS_CLOSING,
      ].includes(action)
    ) {
      const daysToAdd =
        entity[0].interval === IntervalUnit.WEEK
          ? 7
          : entity[0].interval === IntervalUnit.MONTH
          ? 30
          : 1;
      const day = startOfDay(addDays(parseApiDate(updatedAt), daysToAdd));

      const stateToUpdate = {
        filter: {
          closingDate: {
            operator: FilterOperator.IN_RANGE,
            value: [startOfDay(day).toISOString(), endOfDay(day).toISOString()],
          },
        },
      };
      if (pathname === dealListPath) {
        yield put(updateDealView({ viewState: stateToUpdate }));
      } else {
        yield put(push(`${Path.DEAL}/${ViewMode.LIST}`));
        yield put(applyDealListViewSettings(stateToUpdate));
      }
    } else if (MmcNotificationAction.OVERDUE_ACTIVITIES === action) {
      const stateToUpdate = {
        filter: mapActivityStatusOptionToFilter[ActivityStatusOption.OVERDUE] as FilterModel,
        sort: [{ field: activityFieldModel.getByName("startAt")!, order: SortOrder.ASC }],
      };
      if (pathname === activityListPath) {
        yield put(updateActivityView({ viewState: stateToUpdate }));
      } else {
        yield put(push(`${Path.ACTIVITY}/${ViewMode.LIST}`));
        yield put(applyActivityListViewSettings(stateToUpdate));
      }
    } else if (MmcNotificationAction.OUT_OF_CADENCE === action) {
      yield put(showCadenceModal.request(notification));
    }
  }
}

export function* onShowCadenceModal({ payload }: ReturnType<typeof showCadenceModal.request>) {
  try {
    const organizationId: Organization["id"] = yield select(getOrganizationId);
    const companyIds: Company["id"][] = [];
    const peopleIds: Person["id"][] = [];
    const dealIds: Deal["id"][] = [];
    payload.entity!.forEach(({ id, type }) => {
      if (type === EntityType.COMPANY) {
        companyIds.push(id);
      }
      if (type === EntityType.PERSON) {
        peopleIds.push(id);
      }
      if (type === EntityType.DEAL) {
        dealIds.push(id);
      }
    });
    const [companyResponse, peopleResponse, dealResponse]: [
      ListResponse<Company> | undefined,
      ListResponse<Person> | undefined,
      ListResponse<Deal> | undefined
    ] = yield all([
      companyIds.length
        ? callApi("fetchCompanies", organizationId, {
            $filters: {
              $and: [{ id: { $in: companyIds } }],
              includeGroups: true,
            },
          })
        : undefined,
      peopleIds.length
        ? callApi("fetchPeople", organizationId, {
            $filters: {
              $and: [{ id: { $in: peopleIds } }],
              includeGroups: true,
            },
          })
        : undefined,
      dealIds.length
        ? callApi("fetchDeals", organizationId, {
            $filters: {
              $and: [{ id: { $in: dealIds } }],
              includeGroups: true,
            },
          })
        : undefined,
    ]);
    yield put(
      showCadenceModal.success({
        entities: payload
          .entity!.map((notificationEntity) => {
            let entity: Company | Deal | Person | undefined;
            if (notificationEntity.type === EntityType.COMPANY) {
              entity = (companyResponse?.data ?? []).find(({ id }) => id === notificationEntity.id);
            }
            if (notificationEntity.type === EntityType.PERSON) {
              entity = (peopleResponse?.data ?? []).find(({ id }) => id === notificationEntity.id);
            }
            if (notificationEntity.type === EntityType.DEAL) {
              entity = (dealResponse?.data ?? []).find(({ id }) => id === notificationEntity.id);
            }

            return entity
              ? ({
                  ...entity,
                  entity: notificationEntity.type,
                } as OutOfCadenceEntity)
              : undefined;
          })
          .filter(isDefined),
        notification: payload,
      })
    );
  } catch (error) {
    yield put(showCadenceModal.failure(error));
    yield put(handleError({ error }));
  }
}

function* onShowEmailBouncedModal({
  payload: notification,
}: ReturnType<typeof showBouncedEmailModal.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const notificationEntity = notification.entity?.[0];

    let entity: EntitiesSupportedByEmailFeature | undefined;
    if (
      notificationEntity?.type === EntityType.COMPANY ||
      notificationEntity?.type === EntityType.PERSON
    ) {
      const response: ListResponse<EntitiesSupportedByEmailFeature> = yield callApi(
        notificationEntity.type === EntityType.COMPANY ? "fetchCompanies" : "fetchPeople",
        orgId,
        {
          $filters: {
            $and: [{ id: { $eq: notificationEntity.id } }],
            includeAccessStatus: true,
            includeEmailSubscriptionStatus: true,
          },
        }
      );
      entity = response.data[0];
    }

    yield put(showBouncedEmailModal.success({ entity, notification }));
  } catch (error) {
    yield put(handleError({ error }));
    yield put(showBouncedEmailModal.failure());
  }
}

function* onUnsubscribeBouncedEmailEntity() {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const entity: EntitiesSupportedByEmailFeature = yield select(getBouncedEmailEntity);

    yield callApi("unsubscribeEntityEmail", orgId, entity.entity, entity.id);

    yield put(unsubscribeBouncedEmailEntity.success());
  } catch (error) {
    yield put(handleError({ error }));
    yield put(unsubscribeBouncedEmailEntity.failure());
  }
}

function* onUpdateBouncedEmailEntityEmail({
  payload: { email, onSuccess },
}: ReturnType<typeof updateBouncedEmailEntityEmail.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const entity: EntitiesSupportedByEmailFeature = yield select(getBouncedEmailEntity);

    const updatedEntity: EntitiesSupportedByEmailFeature = yield callApi(
      entity.entity === EntityType.COMPANY ? "updateCompany" : "updatePerson",
      orgId,
      undefined,
      {
        id: entity.id,
        email,
      }
    );

    yield put(updateBouncedEmailEntityEmail.success());

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(notifyAboutChanges({ entityType: entity.entity, updated: [updatedEntity] }));
  } catch (error) {
    yield put(handleError({ error }));
    yield put(updateBouncedEmailEntityEmail.success());
  }
}

function* onShowNotifications() {
  // reload notifications every time popover is shown
  yield put(fetchNotifications.request({ onlyMentions: false, reload: true }));
}

function* fetchNotificationsPeriodically() {
  while (true) {
    // only fetch when token is defined
    if (authService.hasToken()) {
      yield put(fetchNotificationsUnreadTotal.request());
    }
    yield delay(60000);
  }
}

export function* notificationSaga() {
  yield takeEvery(clickOnNotification, onClickOnNotification);
  yield takeEvery(fetchNotifications.request, onFetchNotifications);
  yield takeEvery(fetchNotification.request, onGetNotification);
  yield takeEvery(fetchNotificationsUnreadTotal.request, onFetchNotificationsUnreadTotal);
  yield takeEvery(updateNotificationReadStatus.request, onUpdateNotificationReadStatus);
  yield takeEvery(markNotificationsAsRead.request, onMarkNotificationsAsRead);
  yield takeEvery(showCadenceModal.request, onShowCadenceModal);
  yield takeLatest(showNotifications, onShowNotifications);
  yield takeLatest(showBouncedEmailModal.request, onShowEmailBouncedModal);
  yield takeLatest(unsubscribeBouncedEmailEntity.request, onUnsubscribeBouncedEmailEntity);
  yield takeLatest(updateBouncedEmailEntityEmail.request, onUpdateBouncedEmailEntityEmail);
  yield fork(fetchNotificationsPeriodically);
}
